import { useScrollReveal } from '../../hooks/useScrollReveal';

export interface QA {
  question: string;
  answer: string;
}

export interface AIAnswerBlockProps {
  eyebrow: string;
  title: string;
  qas: QA[];
  image?: string;
  imageAlt?: string;
}

export default function AIAnswerBlock({ eyebrow, title, qas, image, imageAlt }: AIAnswerBlockProps) {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();

  return (
    <section className="bg-charcoal py-28 lg:py-40 relative overflow-hidden">
      {/* Subtle background texture */}
      <div
        className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 47px, rgba(201,168,76,0.5) 47px, rgba(201,168,76,0.5) 48px)',
        }}
      />

      <div className="relative z-10 mx-auto px-6 lg:px-12 xl:px-20">
        <div
          ref={ref}
          className={`transition-all duration-700 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex flex-col lg:flex-row lg:items-stretch gap-16 lg:gap-24">
            {/* Left column */}
            <div className="lg:w-5/12 flex-shrink-0 flex flex-col lg:self-stretch">
              <div className="flex items-center gap-4 mb-6">
                <span className="block w-8 h-px bg-gold flex-shrink-0" />
                <span className="text-gold text-[9px] font-semibold tracking-[0.38em] uppercase">
                  {eyebrow}
                </span>
              </div>
              <h2
                className="font-serif text-white leading-tight"
                style={{ fontSize: 'clamp(1.8rem, 3.2vw, 2.8rem)', fontWeight: 300 }}
              >
                {title}
              </h2>
              <div className="w-12 h-px bg-gold mt-8 mb-8" />

              {/* Image fills all remaining left-column height */}
              {image && (
                <div className="relative overflow-hidden flex-1" style={{ minHeight: '320px' }}>
                  <img
                    src={image}
                    alt={imageAlt ?? 'Southeast Lighting & Design — commercial project'}
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-r from-charcoal/20 to-transparent" />
                  <div className="absolute inset-0 border border-gold/15 pointer-events-none" />
                </div>
              )}
            </div>

            {/* Q&A column */}
            <div className="lg:w-7/12 space-y-0">
              {qas.map((qa, i) => (
                <div
                  key={i}
                  className="border-b border-white/8 py-9 last:border-b-0"
                >
                  <div className="flex items-start gap-5">
                    <span className="font-serif text-gold/50 text-sm font-light flex-shrink-0 mt-1">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <p className="text-white text-base font-medium leading-snug mb-4">
                        {qa.question}
                      </p>
                      <p className="text-white/50 text-sm leading-[1.95] font-light">
                        {qa.answer}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
